import { Table } from '@statsbomb/kitbag-components';
import { useAtom } from 'jotai';
import { PropTypes } from 'prop-types';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { styled } from 'styled-components';
import {
  editingQAChecklistAtom,
  metadataIsUpdatingAtom,
} from '../../atoms/approval';
import { selectedMetadataAtom } from '../../atoms/selectedMetadata';
import useApi, { API_ENDPOINTS } from '../../hooks/api';
import useVideoListings from '../../hooks/videoListings';
import { videoReferencePropTypes } from '../../types/videoReference';
import { EditButton } from '../EditButton/EditButton';
import MetadataSelections from '../MetadataSelections';
import Period from '../Period';
import Track from '../Track';

const Container = styled.div`
  display: flex;
  justify-content: space-between;
`;

export function QAChecklist({ editable, videoReference, videoKey }) {
  const api = useApi();
  const intl = useIntl();

  const [selectedMetadata, setSelectedMetadata] = useAtom(selectedMetadataAtom);
  const [editingQAChecklist, setEditingQAChecklist] = useAtom(
    editingQAChecklistAtom,
  );
  const [metadataIsUpdating, setMetadataIsUpdating] = useAtom(
    metadataIsUpdatingAtom,
  );

  const { refreshVideosAwaitingApproval } = useVideoListings();

  const handleSetEditing = (editing) => {
    if (editing) {
      setSelectedMetadata({
        ...videoReference,
        period: videoReference.matchPart,
      });
    }
    setEditingQAChecklist(editing);
  };

  const formSubmit = async () => {
    setMetadataIsUpdating(true);
    await api.patch(`${API_ENDPOINTS.videoService}/videos/${videoKey}`, {
      data: JSON.stringify({
        match_id: selectedMetadata.matchId,
        part: selectedMetadata.period,
        sport: selectedMetadata.sport,
        track: selectedMetadata.track,
        version: selectedMetadata.version,
      }),
    });
    await refreshVideosAwaitingApproval();
    setMetadataIsUpdating(false);
    setEditingQAChecklist(false);
  };

  return (
    <Table>
      <Table.Head>
        <Table.HeaderCell colSpan={2}>
          <Container>
            <FormattedMessage defaultMessage="Match Data" id="ba6f1" />
            {editable && videoKey && (
              <EditButton
                editing={editingQAChecklist}
                editTitle={intl.formatMessage({
                  defaultMessage: 'Edit QA Data',
                  id: '31ac7',
                })}
                isPending={metadataIsUpdating}
                onEdit={handleSetEditing}
                onSave={formSubmit}
                saveDisabled={api.isFetching}
              />
            )}
          </Container>
        </Table.HeaderCell>
      </Table.Head>
      <Table.Body>
        <Table.Row>
          <Table.DataCell $textAlign="left">
            <FormattedMessage defaultMessage="Sport" id="96faa" />
          </Table.DataCell>
          <Table.DataCell $textAlign="left">
            {editingQAChecklist ? (
              <MetadataSelections.Sport size="tiny" />
            ) : (
              videoReference.sport
            )}
          </Table.DataCell>
        </Table.Row>
        <Table.Row>
          <Table.DataCell $textAlign="left">
            <FormattedMessage defaultMessage="Match Id" id="d7b6f" />
          </Table.DataCell>
          <Table.DataCell $textAlign="left">
            {editingQAChecklist ? (
              <MetadataSelections.MatchId size="tiny" />
            ) : (
              videoReference.matchId
            )}
          </Table.DataCell>
        </Table.Row>
        <Table.Row>
          <Table.DataCell $textAlign="left">
            <FormattedMessage defaultMessage="Period" id="19016" />
          </Table.DataCell>
          <Table.DataCell $textAlign="left">
            {editingQAChecklist ? (
              <MetadataSelections.MatchPart size="tiny" />
            ) : (
              <Period
                period={videoReference.matchPart}
                sport={videoReference.sport}
              />
            )}
          </Table.DataCell>
        </Table.Row>
        <Table.Row>
          <Table.DataCell $textAlign="left">
            <FormattedMessage defaultMessage="Track" id="92ea7" />
          </Table.DataCell>
          <Table.DataCell $textAlign="left">
            {editingQAChecklist ? (
              <MetadataSelections.Track size="tiny" />
            ) : (
              <Track track={videoReference.track} />
            )}
          </Table.DataCell>
        </Table.Row>
        <Table.Row>
          <Table.DataCell $textAlign="left">
            <FormattedMessage defaultMessage="Version" id="34b6c" />
          </Table.DataCell>
          <Table.DataCell $textAlign="left">
            {editingQAChecklist ? (
              <MetadataSelections.Version size="tiny" />
            ) : (
              <FormattedMessage
                defaultMessage="v{version}"
                id="d8982"
                values={{ version: videoReference.version }}
              />
            )}
          </Table.DataCell>
        </Table.Row>
      </Table.Body>
    </Table>
  );
}
QAChecklist.propTypes = {
  editable: PropTypes.bool,
  videoKey: PropTypes.string.isRequired,
  videoReference: PropTypes.shape(videoReferencePropTypes).isRequired,
};
QAChecklist.defaultProps = {
  editable: false,
};

export default { QAChecklist };
